// services/socketService.js
import io from "socket.io-client";

const SOCKET_SERVER_URL = process.env.REACT_APP_WS_URL; // Replace with your server URL

class SocketService {
  constructor() {
    if (!SocketService.instance) {
      this.socket = null;
      this.rooms = new Set(); // Track all joined rooms
      this.roomReferences = new Map(); // Track reference count for each room
      this.subscriptions = new Map(); // Track all subscriptions with their callbacks
      this.pendingRooms = new Set(); // Rooms to join when connected
      this.isInitialized = false;

      // Debug message log (sent + received), capped ring buffer
      this.messageLog = [];
      this.messageLogLimit = 100;
      this.logListeners = new Set();
      this._logSeq = 0;

      // Connected live-screen (TV) presence, keyed by device screen id.
      // Fed by "screen-connected" / "screen-disconnected" store-update messages
      // and pruned when a screen stops sending heartbeats.
      this.connectedScreens = new Map(); // screenId -> { ...details, lastSeen }
      this.screenListeners = new Set();
      this.screenStaleMs = 20000; // consider a screen offline after this silence
      this._screenPruneTimer = null;

      SocketService.instance = this;
    }

    return SocketService.instance;
  }

  initialize() {
    if (this.isInitialized && this.socket) {
      return;
    }

    this.socket = io(SOCKET_SERVER_URL, {
      withCredentials: true,
    });

    this.socket.on("connect", () => {
      console.log("Connected to WebSocket server", this.socket.id);

      this.socket.emit("connection-data", {
        version: process.env.REACT_APP_VERSION,
        os: navigator.platform,
        path: window.location.href,
      });

      // Re-auth every room already joined (fresh socket id after a reconnect
      // means the server has no memory of our previous room membership).
      this.rooms.forEach((roomId) => {
        console.log("Re-joining room on connect", roomId);
        this.socket.emit("auth", roomId);
      });

      // Join all pending rooms
      this.pendingRooms.forEach((roomId) => {
        this._joinRoomInternal(roomId);
      });
      this.pendingRooms.clear();
    });

    // Reconnection events live on the Manager (this.socket.io) in socket.io v4,
    // not on the socket instance.
    this.socket.io.on("reconnect", (attempt) => {
      console.log("Socket reconnected after", attempt, "attempts");
    });

    this.socket.io.on("reconnect_attempt", (attempt) => {
      console.log("Socket reconnect attempted", attempt);
    });

    this.socket.on("disconnect", () => {
      console.log("Socket disconnected", this.socket.id);
    });

    // Capture every incoming event for the debug message log
    this.socket.onAny((topic, ...args) => {
      this._logMessage("received", topic, args.length <= 1 ? args[0] : args);
      // Track live-screen presence from store-update broadcasts.
      if (topic === "store-update") {
        this._handleScreenPresence(args[0]);
      }
    });

    this._startScreenPrune();

    this.isInitialized = true;
  }

  // ---- Live-screen presence -------------------------------------------------

  _parseScreenPayload(message) {
    if (!message) return null;
    if (typeof message === "string") {
      try {
        return JSON.parse(message);
      } catch {
        return null;
      }
    }
    return typeof message === "object" ? message : null;
  }

  _handleScreenPresence(data) {
    if (!data || typeof data !== "object") return;
    const inner = data.topic;
    if (inner !== "screen-connected" && inner !== "screen-disconnected") return;

    const screen = this._parseScreenPayload(data.message);
    if (!screen || !screen.id) return;

    if (inner === "screen-connected") {
      const existing = this.connectedScreens.get(screen.id);
      this.connectedScreens.set(screen.id, {
        ...existing,
        ...screen,
        connectedAt: existing?.connectedAt || screen.connectedAt || Date.now(),
        lastSeen: Date.now(),
      });
    } else {
      this.connectedScreens.delete(screen.id);
    }
    this._emitScreens();
  }

  _startScreenPrune() {
    if (this._screenPruneTimer) return;
    this._screenPruneTimer = setInterval(() => {
      const now = Date.now();
      let changed = false;
      this.connectedScreens.forEach((screen, id) => {
        if (now - (screen.lastSeen || 0) > this.screenStaleMs) {
          this.connectedScreens.delete(id);
          changed = true;
        }
      });
      if (changed) this._emitScreens();
    }, 5000);
  }

  _emitScreens() {
    const list = this.getConnectedScreens();
    this.screenListeners.forEach((listener) => {
      try {
        listener(list);
      } catch (err) {
        console.error("Screen presence listener error", err);
      }
    });
  }

  getConnectedScreens() {
    return Array.from(this.connectedScreens.values());
  }

  /**
   * Subscribe to connected-screen changes.
   * @param {(screens: Array) => void} listener
   * @returns {() => void} unsubscribe function
   */
  subscribeToScreens(listener) {
    this.screenListeners.add(listener);
    return () => this.screenListeners.delete(listener);
  }

  // ---- Debug message log ----------------------------------------------------

  _logMessage(direction, topic, data) {
    const entry = {
      id: ++this._logSeq,
      direction, // "sent" | "received"
      topic,
      data,
      timestamp: Date.now(),
    };

    this.messageLog.push(entry);
    if (this.messageLog.length > this.messageLogLimit) {
      this.messageLog.shift();
    }

    this.logListeners.forEach((listener) => {
      try {
        listener(this.messageLog);
      } catch (err) {
        console.error("Socket log listener error", err);
      }
    });
  }

  getMessageLog() {
    return this.messageLog;
  }

  clearMessageLog() {
    this.messageLog = [];
    this.logListeners.forEach((listener) => listener(this.messageLog));
  }

  /**
   * Subscribe to message-log changes.
   * @param {(log: Array) => void} listener
   * @returns {() => void} unsubscribe function
   */
  subscribeToLog(listener) {
    this.logListeners.add(listener);
    return () => this.logListeners.delete(listener);
  }

  _joinRoomInternal(roomId) {
    if (this.socket && this.socket.connected) {
      // Only join if not already in the room
      if (!this.rooms.has(roomId)) {
        console.log("Joining room", roomId);
        this.socket.emit("auth", roomId);
        this.rooms.add(roomId);
        this.roomReferences.set(roomId, 1);
      } else {
        // Increment reference count
        const currentCount = this.roomReferences.get(roomId) || 0;
        this.roomReferences.set(roomId, currentCount + 1);
        console.log(
          "Room already joined, incrementing reference count",
          roomId,
          currentCount + 1
        );
      }
    }
  }

  getSocket() {
    return this.socket;
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  getJoinedRooms() {
    return Array.from(this.rooms);
  }

  joinRoom(roomId) {
    if (!roomId) return;

    this.initialize();

    // If already in room, just increment reference count
    if (this.rooms.has(roomId)) {
      const currentCount = this.roomReferences.get(roomId) || 0;
      this.roomReferences.set(roomId, currentCount + 1);
      console.log(
        "Room already joined, incrementing reference count",
        roomId,
        currentCount + 1
      );
      return;
    }

    if (this.socket?.connected) {
      this._joinRoomInternal(roomId);
    } else {
      // If socket is not yet connected, save the room ID to join later
      // Only add if not already pending
      if (!this.pendingRooms.has(roomId)) {
        this.pendingRooms.add(roomId);
      }
    }
  }

  leaveRoom(roomId, force = false) {
    if (!roomId) return;

    // Only leave if actually in the room
    if (!this.rooms.has(roomId) && !force) {
      console.log("Not in room", roomId);
      return;
    }

    // Decrement reference count
    const currentCount = this.roomReferences.get(roomId) || 1;
    const newCount = currentCount - 1;
    this.roomReferences.set(roomId, newCount);

    // Only actually leave the room if no more references
    if (newCount <= 0 || force) {
      console.log("Leaving room", roomId, force ? "(forced)" : "");
      if (this.socket?.connected) {
        this.socket.emit("logout", roomId);
      }
      this.rooms.delete(roomId);
      this.roomReferences.delete(roomId);
    } else {
      console.log(
        "Room still in use, decrementing reference count",
        roomId,
        newCount
      );
    }

    this.pendingRooms.delete(roomId);
  }

  sendMessage(topic, message, storeId) {
    if (!this.socket?.connected) {
      console.warn("Socket not connected, cannot send message");
      return;
    }

    const storeIdToUse = storeId || Array.from(this.rooms)[0];
    console.log("Sending message", {
      topic,
      message,
      storeId: storeIdToUse,
    });
    this.socket.emit("store-update", {
      topic,
      storeId: storeIdToUse,
      message,
    });
    this._logMessage("sent", topic, { message, storeId: storeIdToUse });
  }

  sendGeneralMessage(topic, message) {
    if (!this.socket?.connected) {
      console.warn("Socket not connected, cannot send message");
      return;
    }

    console.log("Sending general message", { topic, message });
    this.socket.emit("general-update", {
      topic,
      message,
    });
    this._logMessage("sent", topic, { message });
  }

  subscribeToMessages(topic, callback, id = null) {
    if (!this.socket) {
      this.initialize();
    }

    const subscriptionId = id || `${topic}-${Date.now()}-${Math.random()}`;

    // Store subscription with ID for easy cleanup
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Map());
    }
    this.subscriptions.get(topic).set(subscriptionId, callback);

    this.socket.on(topic, callback);

    return subscriptionId; // Return ID for cleanup
  }

  unsubscribeFromMessages(topic, subscriptionId = null) {
    if (!this.socket) return;

    if (subscriptionId) {
      // Remove specific subscription
      const topicSubs = this.subscriptions.get(topic);
      if (topicSubs) {
        const callback = topicSubs.get(subscriptionId);
        if (callback) {
          this.socket.off(topic, callback);
          topicSubs.delete(subscriptionId);
          if (topicSubs.size === 0) {
            this.subscriptions.delete(topic);
          }
        }
      }
    } else {
      // Remove all subscriptions for this topic
      const topicSubs = this.subscriptions.get(topic);
      if (topicSubs) {
        topicSubs.forEach((callback) => {
          this.socket.off(topic, callback);
        });
        this.subscriptions.delete(topic);
      }
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.rooms.clear();
      this.roomReferences.clear();
      this.pendingRooms.clear();
      this.subscriptions.clear();
      this.connectedScreens.clear();
      this._emitScreens();
      if (this._screenPruneTimer) {
        clearInterval(this._screenPruneTimer);
        this._screenPruneTimer = null;
      }
      this.isInitialized = false;
    }
  }
}

const socketService = new SocketService();

export default socketService;
